import { useRef, useEffect } from "react";
import { Box, Grid, Paper, ThemeProvider } from "@mui/material";
import { EventsFilterSidebar, ActiveFilters } from "@/components/Events";
import {
  EventsToolbar,
  EventsTableBody,
} from "@/components/Events/EventsTable.tsx";
import { useEvents, useFilterMetadata, useSnapshotInfo } from "@/hooks/useDashboard.ts";
import { useEventFilters } from "@/hooks/useEventFilters.ts";
import { useExportCsv } from "@/hooks/useExportCsv.ts";
import type { AsnCount, CountryCount, IpEvent, SortField, SortOrder } from "@/types";
import { colors, cardStyles, eventsLightTheme } from "@/theme/eventsTokens.ts";

const ROWS_PER_PAGE = 50;

function buildPagination(page: number, total: number) {
  return {
    page,
    pageCount: Math.max(1, Math.ceil(total / ROWS_PER_PAGE)),
    total,
    showFrom: total === 0 ? 0 : page * ROWS_PER_PAGE + 1,
    showTo: Math.min((page + 1) * ROWS_PER_PAGE, total),
  };
}

function parseRetentionMinDate(dateStr?: string): Date | undefined {
  return dateStr ? new Date(dateStr) : undefined;
}

/**
 * Renders the "IP Activity" page (route: /ip-activity). File and symbol are
 * kept as "Events" because the underlying data model is events-per-IP — the
 * page heading and URL describe the user-facing concept, while these symbols
 * describe the domain.
 */
export default function EventsPage() {
  const filters = useEventFilters();
  const { data, isLoading, isFetching, error } = useEvents(filters.apiFilters, ROWS_PER_PAGE, filters.page * ROWS_PER_PAGE);
  const { data: filterMetadata } = useFilterMetadata();
  const { data: snapshotInfo } = useSnapshotInfo();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pagination = buildPagination(filters.page, total);
  const retentionMinDate = parseRetentionMinDate(snapshotInfo?.retention_min_date);
  const handleExportCsv = useExportCsv(items);

  return (
    <ThemeProvider theme={eventsLightTheme}>
      <Box
        sx={{
          backgroundColor: colors.backgroundSecondary,
          minHeight: "calc(100vh - 60px)",
          p: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ActiveFilters filters={filters.apiFilters} onRemoveFilter={filters.handleRemoveFilter} />
        <EventsGrid filters={filters} items={items} isLoading={isLoading} isFetching={isFetching} error={error} pagination={pagination} onExport={handleExportCsv} countryOptions={filterMetadata?.country_codes} asnOptions={filterMetadata?.asns} lastUpdated={snapshotInfo?.last_updated} retentionDays={snapshotInfo?.retention_days} retentionMinDate={retentionMinDate} sortBy={filters.sortBy} sortOrder={filters.sortOrder} onSort={filters.handleSort} />
      </Box>
    </ThemeProvider>
  );
}

interface EventsGridProps {
  readonly filters: ReturnType<typeof useEventFilters>;
  readonly items: IpEvent[];
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly error: Error | null;
  readonly pagination: { page: number; pageCount: number; total: number; showFrom: number; showTo: number };
  readonly onExport: () => void;
  readonly countryOptions?: CountryCount[];
  readonly asnOptions?: AsnCount[];
  readonly lastUpdated?: string;
  readonly retentionDays?: number;
  readonly retentionMinDate?: Date;
  readonly sortBy: SortField;
  readonly sortOrder: SortOrder;
  readonly onSort: (field: SortField) => void;
}

function EventsGrid({ filters, isLoading, isFetching, error, items, pagination, onExport, countryOptions, asnOptions, lastUpdated, retentionDays, retentionMinDate, sortBy, sortOrder, onSort }: EventsGridProps) {
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tableRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [pagination.page]);

  return (
    <Grid container spacing={3} sx={{ flex: 1 }}>
      <Grid size={{ sm: 12, lg: 3 }}>
        <EventsFilterSidebar
          filters={filters.panelFilters}
          onFiltersChange={filters.setPanelFilters}
          onApply={filters.handleApply}
          onReset={filters.handleReset}
          countryOptions={countryOptions}
          asnOptions={asnOptions}
          retentionMinDate={retentionMinDate}
          retentionDays={retentionDays}
          isFetching={isFetching}
        />
      </Grid>
      <Grid size={{ sm: 12, lg: 9 }} sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Paper
          elevation={0}
          sx={{ ...cardStyles.default, display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
        >
          <EventsToolbar
            page={pagination.page}
            pageCount={pagination.pageCount}
            total={pagination.total}
            showFrom={pagination.showFrom}
            showTo={pagination.showTo}
            onPrev={() => filters.setPage((p) => p - 1)}
            onNext={() => filters.setPage((p) => p + 1)}
            onExport={onExport}
            lastUpdated={lastUpdated}
            isFetching={isFetching}
          />
          <Box ref={tableRef} sx={{ flex: 1, overflow: "auto" }}>
            <EventsTableBody items={items} isLoading={isLoading} isFetching={isFetching} error={error} sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}
